import * as Yup from "yup";
import PublicLayout from "sections/PublicLayout";
import SteppedForm from "sections/public/SteppedForm";
import { submitTutorsData } from "firestoreService";
import {
  APPLICANT_ID_MAX_BYTES,
  APPLICANT_PDF_MAX_BYTES,
  APPLICANT_PHOTO_MAX_BYTES,
  DOCUMENT_TYPES,
  IMAGE_TYPES,
  applicantIdLimit,
  formatBytes,
} from "utils/uploads";

const STEPS = [
  {
    label: "About you",
    description: "Your basic details, so we know who we are speaking to.",
    fields: [
      { name: "name", label: "Full name", type: "text", required: true, maxLength: 120 },
      { name: "email", label: "Email address", type: "email", required: true, maxLength: 200 },
      {
        name: "mobileNo",
        label: "Mobile number",
        type: "tel",
        required: true,
        maxLength: 10,
      },
      {
        name: "alternateMobileNo",
        label: "Alternate mobile number",
        type: "tel",
        required: true,
        maxLength: 10,
      },
      { name: "age", label: "Age", type: "number", required: true },
      {
        name: "gender",
        label: "Gender",
        type: "radio",
        required: true,
        options: [
          { value: "female", label: "Female" },
          { value: "male", label: "Male" },
        ],
      },
      {
        name: "maritalStatus",
        label: "Marital status",
        type: "radio",
        required: true,
        options: [
          { value: "married", label: "Married" },
          { value: "unmarried", label: "Unmarried" },
        ],
      },
      {
        name: "address",
        label: "Full address",
        type: "textarea",
        required: true,
        full: true,
        maxLength: 500,
      },
    ],
  },
  {
    label: "Teaching",
    description: "What you teach and where you can travel.",
    fields: [
      {
        name: "qualification",
        label: "Highest qualification",
        type: "text",
        required: true,
        maxLength: 200,
      },
      { name: "experience", label: "Experience (in years)", type: "number", required: true },
      {
        name: "subjects",
        label: "Classes and subjects you teach",
        type: "text",
        full: true,
        maxLength: 500,
        helper: "Separate multiple subjects with commas.",
      },
      {
        name: "areaVisit",
        label: "Areas you can travel to",
        type: "textarea",
        required: true,
        full: true,
        maxLength: 500,
      },
      {
        name: "transportMode",
        label: "Mode of transport",
        type: "text",
        required: true,
        maxLength: 50,
      },
      { name: "fees", label: "Expected fees", type: "text", required: true, maxLength: 50 },
      {
        name: "engComm",
        label: "Comfortable communicating in English?",
        type: "radio",
        required: true,
        options: [
          { value: "Yes", label: "Yes" },
          { value: "No", label: "No" },
        ],
      },
      {
        name: "refrence",
        label: "Reference (optional)",
        type: "text",
        maxLength: 200,
      },
    ],
  },
  {
    label: "Documents",
    description: "A clear photo and one ID proof. Both stay private to our team.",
    fields: [
      { name: "photo", label: "Your photo", type: "photo", required: true, full: true },
      {
        name: "idProof",
        label: "ID proof (Aadhaar, PAN, voter ID or similar)",
        type: "document",
        required: true,
        full: true,
        helper:
          `png, jpg, jpeg, webp or pdf. Photos are reduced to ${formatBytes(
            APPLICANT_ID_MAX_BYTES
          )}. ` + `A PDF must be under ${formatBytes(APPLICANT_PDF_MAX_BYTES)}.`,
      },
    ],
  },
  {
    label: "Declaration",
    description:
      "Please confirm before we add you to our panel. The date of registration is recorded automatically when you submit.",
    fields: [
      {
        name: "declaration",
        label:
          "I declare that the information above is true, and I agree to be contacted by A1 Home Tutors about tuition assignments.",
        type: "checkbox",
        full: true,
      },
    ],
  },
];

const validationSchema = Yup.object().shape({
  photo: Yup.mixed()
    .required("Photo is required")
    .test("fileType", "Unsupported file format (only png, jpg, jpeg, webp)", (value) =>
      !value ? true : IMAGE_TYPES.includes(value.type)
    )
    .test(
      "fileSize",
      `Photo is too large (max ${formatBytes(APPLICANT_PHOTO_MAX_BYTES)} after compression)`,
      (value) => !value || value.size <= APPLICANT_PHOTO_MAX_BYTES
    ),
  idProof: Yup.mixed()
    .required("ID proof is required")
    .test("fileType", "Unsupported file format (only png, jpg, jpeg, webp, pdf)", (value) =>
      !value ? true : DOCUMENT_TYPES.includes(value.type)
    )
    .test("fileSize", "ID proof is too large", function (value) {
      if (!value) return true;
      const limit = applicantIdLimit(value);
      if (value.size <= limit) return true;
      const kind = value.type === "application/pdf" ? "PDF" : "ID photo";
      return this.createError({
        message: `${kind} is too large (max ${formatBytes(limit)}${
          kind === "PDF" ? "" : " after compression"
        })`,
      });
    }),
  name: Yup.string().trim().max(120).required("Name is required"),
  email: Yup.string().trim().email("Invalid email format").max(200).required("Email is required"),
  age: Yup.number().positive("Age must be positive").required("Age is required"),
  gender: Yup.string().required("Gender is required"),
  mobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  alternateMobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, "Alternate mobile number must be 10 digits")
    .required("Alternate mobile number is required"),
  experience: Yup.number()
    .min(0, "Experience must be at least 0")
    .required("Experience is required"),
  address: Yup.string().trim().max(500).required("Address is required"),
  qualification: Yup.string().trim().max(200).required("Qualification is required"),
  areaVisit: Yup.string().trim().max(500).required("Preferred area is required"),
  maritalStatus: Yup.string().required("Marital status is required"),
  fees: Yup.string().trim().max(50).required("Expected fees is required"),
  transportMode: Yup.string().trim().max(50).required("Mode of transport is required"),
  engComm: Yup.string().required("Please answer this question"),
  // firestore.rules requires `declaration` to be a bool, and the point of the
  // step is that it must be ticked.
  declaration: Yup.boolean().oneOf([true], "Please accept the declaration to continue"),
});

const initialValues = {
  photo: null,
  idProof: null,
  name: "",
  email: "",
  refrence: "",
  age: "",
  gender: "",
  mobileNo: "",
  alternateMobileNo: "",
  experience: "",
  address: "",
  subjects: "",
  qualification: "",
  areaVisit: "",
  maritalStatus: "",
  engComm: "",
  transportMode: "",
  fees: "",
  declaration: false,
};

function TutorRegistration() {
  return (
    <PublicLayout
      eyebrow="Join our panel"
      title="Tutor Registration"
      subtitle="Four short steps. We will call you once your details are verified."
      sheet={false}
    >
      <SteppedForm
        steps={STEPS}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={submitTutorsData}
        successTitle="Registration received"
        successMessage="Thank you for registering as a tutor. Our team will verify your details and call you shortly."
      />
    </PublicLayout>
  );
}

export default TutorRegistration;
